import React, { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout";
import { api } from "../../utils/api";
import {
  MoreVertical,
  Search,
  Filter,
  UserCheck,
  UserX,
  Mail,
  RefreshCw,
  Users,
  Key
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import Modal from "../../components/ui/modal.jsx";
import { Button } from "../../components/ui/button.jsx";
import { useToast } from "../../contexts/ToastContext";

export default function AgentManagement() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const { showToast } = useToast();

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const res = await api.adminListAgents();
      setAgents(res.data?.users || []);
    } catch (err) {
      toast.error("Failed to load users");
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (email) => {
    try {
      await api.approveAgents(email);
      showToast("Agent Approved Successfully", "success");
      setShowDisableModal(true);
      fetchAgents();
    } catch (err) {
      toast.error("Failed to approve agent");
    }
  };

  const handleDisable = async (email) => {
    try {
      await api.disableAgents(email);
      setShowPendingModal(true);
      fetchAgents();
    } catch (err) {
      toast.error("Failed to disable agent");
    }
  };

  const handleVerificationLink = async (email) => {
    try {
      await api.sendAgentVerificationLink(email);
      toast.success("Verification link sent successfully");
    } catch (err) {
      toast.error("Failed to send verification link");
    }
  };

  const handleResetPassword = async (email) => {
    try {
      await api.adminResetUserPassword(email);
      // toast.success("Password reset link sent successfully");
    } catch (err) {
      toast.error("Failed to send password reset link");
    }
  }

  // Filter agents based on search and status
  const filteredAgents = agents.filter((agent) => {
    const matchesSearch =
      agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.country?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "pending" && agent.is_approved === "10") ||
      (statusFilter === "approved" && agent.is_approved !== "10");

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (isApproved) => {
    if (isApproved === "10") {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
          Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
        Approved
      </span>
    );
  };

  const stats = {
    total: agents.length,
    approved: agents.filter(a => a.is_approved !== "10").length,
    pending: agents.filter(a => a.is_approved === "10").length,
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">Loading agents...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Agent Management</h1>
            <p className="text-gray-600 mt-1">Manage and monitor all agents in the system</p>
          </div>
          
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 uppercase tracking-wide">Total Agents</p>
                <p className="text-3xl font-bold text-blue-900 mt-2">{stats.total}</p>
              </div>
              <div className="p-3 bg-blue-200 rounded-lg">
                <Users className="h-8 w-8 text-blue-700" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-6 border border-emerald-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-600 uppercase tracking-wide">Approved</p>
                <p className="text-3xl font-bold text-emerald-900 mt-2">{stats.approved}</p>
              </div>
              <div className="p-3 bg-emerald-200 rounded-lg">
                <UserCheck className="h-8 w-8 text-emerald-700" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6 border border-amber-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600 uppercase tracking-wide">Pending</p>
                <p className="text-3xl font-bold text-amber-900 mt-2">{stats.pending}</p>
              </div>
              <div className="p-3 bg-amber-200 rounded-lg">
                <UserX className="h-8 w-8 text-amber-700" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or country..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
              >
                <option value="all">All Status</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>
        </div>

        {/* Agents Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Agent Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Location
                  </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {filteredAgents.map((agent) => (
                  <tr key={agent.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">

                      <div className="ml-4">
                        <div className="text-sm font-semibold text-gray-900">{agent.name}</div>
                        <div className="text-sm text-gray-500">{agent.email}</div>
                      </div>

                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{agent.phone || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{agent.country || 'N/A'}</div>
                    </td>
                       <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{agent.address || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(agent.is_approved)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-gray-100"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {agent.is_approved === "10" ? (
                            <DropdownMenuItem
                              onClick={() => handleApprove(agent.email)}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <UserCheck className="h-4 w-4 text-emerald-600" />
                              <span>Approve Agent</span>
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => handleDisable(agent.email)}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <UserX className="h-4 w-4 text-red-600" />
                              <span>Disable Agent</span>
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleVerificationLink(agent.email)}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <Mail className="h-4 w-4 text-blue-600" />
                            <span>Send Verification</span>
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={() => handleResetPassword(agent.email)}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <Key className="h-4 w-4 text-blue-600" />
                            <span>Reset Password</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredAgents.length === 0 && (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No agents found</p>
                <p className="text-gray-400 text-sm mt-1">
                  {searchTerm || statusFilter !== "all"
                    ? "Try adjusting your search or filters"
                    : "Get started by adding your first agent"}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Results count */}
        {filteredAgents.length > 0 && (
          <div className="text-sm text-gray-600 text-center">
            Showing {filteredAgents.length} of {agents.length} agents
          </div>
        )}
      </div>

      {/* Modals */}
      <Modal
        open={showPendingModal}
        title="Agent Disabled"
        message="Agent has been disabled successfully."
        onClose={() => setShowPendingModal(false)}
      />
      <Modal
        open={showDisableModal}
        title="Agent Approved"
        message="Agent has been approved successfully."
        onClose={() => setShowDisableModal(false)}
      />
    </AdminLayout>
  );
}